import type { ProjectImage, ProjectRecord } from "@/types/api";

/**
 * The photos of a record, as an array.
 *
 * The v2.0 read returns the full set on `images[]` (and no top-level `image`);
 * the legacy v1.x reads carry a single `image` and no `images`. This adapter
 * presents both uniformly as an array so the multi-photo UI (3-col grid,
 * fullscreen carousel) reads one shape. Pure and idempotent.
 *
 * Falls back to `[image]` when `images` is absent or empty, and to `[]` when
 * neither is present (a zero-photo record) — never `[undefined]`.
 */
export function recordImages(
  record: Pick<ProjectRecord, "images" | "image">,
): ProjectImage[] {
  return record.images?.length
    ? record.images
    : record.image
      ? [record.image]
      : [];
}

/**
 * The cover photo of a record — the first of `images[]` when present, else the
 * legacy single `image`, else `undefined` (a record with no photos). Every
 * single-image read site (grid tiles, share view, report preview, offline
 * asset keys) resolves its thumbnail/src through this so the cover is correct
 * whether the server sent the v2.0 `images[]` or a legacy `image`.
 */
export function coverImage(record: ProjectRecord): ProjectImage | undefined {
  return record.images?.[0] ?? record.image;
}

/** Number of photos on a record (0 for a record carrying no photo). */
export function recordPhotoCount(record: ProjectRecord): number {
  return record.images?.length ?? (record.image ? 1 : 0);
}
